import ks from "@shared/kalmstromShared4";
import { IListDefinition, IDragOffset } from "./DBInterfaces";
import { CONFIG } from "./DBConstants";

export default class DBState {

    public static Lists: IListDefinition[] = [];
    public static FieldIdCounter: number = 0;
    public static SelectedListId: string | null = null;
    public static EditingListId: string | null = null;
    public static EditingFieldId: string | null = null;
    public static Options: string[] = [];
    public static DraggedType: string | null = null;

    public static DraggedCard: HTMLElement | null = null;
    public static DragOffset: IDragOffset = { X: 0, Y: 0 };

    public static History: string[] = [];
    public static HistoryIndex: number = -1;
    public static MaxHistory: number = 50;

    public static AutoSaveTimeout: ReturnType<typeof setTimeout> | null = null;
    public static AutoSaveDelay: number = 1000;

    public static CurrentOutputContent: string = "";
    public static CurrentOutputFilename: string = "";
    public static CurrentOutputType: string = "";
    public static CurrentOutputFiles: Array<{ name: string; content: string; filename: string; type: string; }> | null = null;

    public static EditingViewListId: string | null = null;
    public static EditingViewId: string | null = null;
    public static DataModalListId: string | null = null;
    public static FieldEditorListId: string | null = null;
    public static ManageViewsListId: string | null = null;

    public static OnRenderLists: (() => void) | null = null;
    public static OnShowToast: ((message: string, type: string) => void) | null = null;

    public static GetAppCode = (): string => {
        if (ks.kErrorMode === true) { return "TC"; }
        try {
            const input: HTMLInputElement | null = document.getElementById("appCodeInput") as HTMLInputElement;
            if (input !== null && input !== undefined) {
                return input.value || CONFIG.DEFAULT_APP_CODE;
            }
            return CONFIG.DEFAULT_APP_CODE;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBState.GetAppCode");
            return CONFIG.DEFAULT_APP_CODE;
        }
    };

    public static FindList = (listId: string): IListDefinition | null => {
        if (ks.kErrorMode === true) { return null; }
        try {
            const FindById = (l: IListDefinition): boolean => {
                return l.Id === listId;
            };
            const result: IListDefinition | undefined = DBState.Lists.find(FindById);
            if (result !== undefined) { return result; }
            return null;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBState.FindList");
            return null;
        }
    };

    public static SaveState = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const state: string = JSON.stringify({
                lists: DBState.Lists,
                fieldIdCounter: DBState.FieldIdCounter
            });

            DBState.History = DBState.History.slice(0, DBState.HistoryIndex + 1);
            DBState.History.push(state);

            if (DBState.History.length > DBState.MaxHistory) {
                DBState.History.shift();
            } else {
                DBState.HistoryIndex++;
            }

            DBState.UpdateUndoRedoButtons();
            DBState.ScheduleAutoSave();
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBState.SaveState");
        }
    };

    public static Undo = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (DBState.HistoryIndex > 0) {
                DBState.HistoryIndex--;
                DBState.RestoreFromHistory(DBState.History[DBState.HistoryIndex]);
                DBState.UpdateUndoRedoButtons();
                if (DBState.OnShowToast !== null) { DBState.OnShowToast("Undo", "success"); }
            } else {
                if (DBState.OnShowToast !== null) { DBState.OnShowToast("Nothing to undo", "error"); }
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBState.Undo");
        }
    };

    public static Redo = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (DBState.HistoryIndex < DBState.History.length - 1) {
                DBState.HistoryIndex++;
                DBState.RestoreFromHistory(DBState.History[DBState.HistoryIndex]);
                DBState.UpdateUndoRedoButtons();
                if (DBState.OnShowToast !== null) { DBState.OnShowToast("Redo", "success"); }
            } else {
                if (DBState.OnShowToast !== null) { DBState.OnShowToast("Nothing to redo", "error"); }
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBState.Redo");
        }
    };

    public static RestoreFromHistory = (stateJson: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const state: { lists: IListDefinition[]; fieldIdCounter: number } = JSON.parse(stateJson);
            DBState.Lists = state.lists;
            DBState.FieldIdCounter = state.fieldIdCounter;
            if (DBState.OnRenderLists !== null) { DBState.OnRenderLists(); }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBState.RestoreFromHistory");
        }
    };

    public static UpdateUndoRedoButtons = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const undoBtn: HTMLButtonElement | null = document.getElementById("undoBtn") as HTMLButtonElement;
            const redoBtn: HTMLButtonElement | null = document.getElementById("redoBtn") as HTMLButtonElement;

            if (undoBtn !== null) {
                undoBtn.disabled = DBState.HistoryIndex <= 0;
            }
            if (redoBtn !== null) {
                redoBtn.disabled = DBState.HistoryIndex >= DBState.History.length - 1;
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBState.UpdateUndoRedoButtons");
        }
    };

    public static ScheduleAutoSave = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (DBState.AutoSaveTimeout !== null) {
                clearTimeout(DBState.AutoSaveTimeout);
            }
            DBState.AutoSaveTimeout = setTimeout(DBState.SaveToStorage, DBState.AutoSaveDelay);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBState.ScheduleAutoSave");
        }
    };

    public static SaveToStorage = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const data: object = {
                lists: DBState.Lists,
                fieldIdCounter: DBState.FieldIdCounter,
                appCode: DBState.GetAppCode(),
                savedAt: new Date().toISOString()
            };
            localStorage.setItem("listSchemaDesigner", JSON.stringify(data));
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBState.SaveToStorage");
        }
    };

    public static LoadFromStorage = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const saved: string | null = localStorage.getItem("listSchemaDesigner");
            if (saved !== null) {
                const data: any = JSON.parse(saved);
                const rawLists: any[] = data.lists || [];
                DBState.Lists = rawLists.map((l: any) => {
                    l.Properties = l.Properties || {};
                    l.Data = l.Data || [];
                    l.Views = l.Views || [];
                    return l as IListDefinition;
                });
                DBState.FieldIdCounter = data.fieldIdCounter || 0;
                if (data.appCode) {
                    const appCodeInput: HTMLInputElement | null = document.getElementById("appCodeInput") as HTMLInputElement;
                    if (appCodeInput !== null) {
                        appCodeInput.value = data.appCode;
                    }
                }
                if (DBState.OnRenderLists !== null) { DBState.OnRenderLists(); }
                const savedDate: Date = new Date(data.savedAt);
                if (DBState.OnShowToast !== null) {
                    DBState.OnShowToast("Loaded from " + savedDate.toLocaleString(), "success");
                }
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBState.LoadFromStorage");
        }
    };
}
